import React, { useEffect, useState } from 'react';
import API from '../../api';
import { Pencil, Trash2 } from 'lucide-react';

export default function NoticeBoard() {
  const [notices, setNotices] = useState([]);
  const [form, setForm] = useState({ heading: '', content: '', date: '' });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchNotices = async () => {
    try {
      const res = await API.get('/notices');
      setNotices(res.data);
    } catch (err) {
      console.error('Error fetching notices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotices();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await API.put(`/notices/${editingId}`, form);
      } else {
        await API.post('/notices', form);
      }
      setForm({ heading: '', content: '', date: '' });
      setEditingId(null);
      fetchNotices();
    } catch (err) {
      console.error('Submit error:', err.response?.data || err.message);
    }
  };

  const handleEdit = (notice) => {
    setForm({
      heading: notice.heading,
      content: notice.content,
      date: notice.date.slice(0, 10),
    });
    setEditingId(notice.id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this notice?')) {
      try {
        await API.delete(`/notices/${id}`);
        fetchNotices();
      } catch (err) {
        console.error('Delete error:', err.response?.data || err.message);
      }
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">📢 Notice Board</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left: Form */}
        <div className="bg-white shadow-md rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-4">{editingId ? 'Edit Notice' : 'Create Notice'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              name="heading"
              placeholder="Notice Heading"
              className="border border-gray-300 p-3 rounded-lg w-full"
              value={form.heading}
              onChange={handleChange}
              required
            />
            <input
              type="date"
              name="date"
              className="border border-gray-300 p-3 rounded-lg w-full"
              value={form.date}
              onChange={handleChange}
              required
            />
            <textarea
              name="content"
              placeholder="Write the notice content here..."
              className="w-full border border-gray-300 p-3 rounded-lg h-40 resize-none"
              value={form.content}
              onChange={handleChange}
              required
            ></textarea>
            <div className="text-right">
              <button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition"
              >
                {editingId ? 'Update Notice' : 'Post Notice'}
              </button>
            </div>
          </form>
        </div>

        {/* Right: Notices List */}
        <div className="space-y-4">
          {loading ? (
            <p className="text-gray-600">Loading notices...</p>
          ) : notices.length === 0 ? (
            <p className="text-gray-500">No notices posted yet.</p>
          ) : (
            notices.map((notice) => (
              <div
                key={notice.id}
                className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex justify-between items-start hover:shadow-md transition"
              >
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-800">{notice.heading}</h3>
                  <p className="text-gray-700 text-sm mt-1 whitespace-pre-line">{notice.content}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Date: {new Date(notice.date).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(notice)}
                    className="text-blue-600 hover:text-blue-800 p-2 rounded hover:bg-blue-50 transition"
                    title="Edit"
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(notice.id)}
                    className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-50 transition"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
